import { Link, useParams } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { PageHeader } from '../components/common/PageHeader';
import { Button } from '../components/common/Button';
import { Icon } from '../components/common/Icon';
import { Reveal } from '../components/animation/Reveal';
import { FaqBlock } from '../components/product/ProductFactPanels';
import { knowledgeArticle, knowledgeArticles } from '../data/knowledge';
import { useProducts, useCategories } from '../hooks';
import { canonicalProductSlug } from '../lib/slug';
import { articleSchema, breadcrumbSchema, faqSchema } from '../lib/seo';

/**
 * One knowledge article.
 *
 * The structure is deliberate and is the whole answer-engine strategy on this site: H1 phrased as the
 * question, then ONE self-contained paragraph that answers it completely, then the depth. An assistant
 * that reads only the opening gets a correct answer; a reader who wants the mechanism reads on. The
 * GreenbayBio products appear at the END, as links under a heading that says what they are — never
 * woven through the science, where a general statement about beneficial microbes would read as a claim
 * about a particular bottle.
 */
export default function KnowledgeArticlePage() {
  const { slug = '' } = useParams();
  const article = knowledgeArticle(slug);
  const { data: products = [] } = useProducts();
  const { data: categories = [] } = useCategories();

  if (!article) {
    return (
      <>
        <PageHeader title="Article not found" subtitle="We don’t have an article at that address." />
        <div className="container-x py-16">
          <Button to="/knowledge" variant="primary" size="lg">
            <Icon name="ArrowLeft" className="h-5 w-5" /> Knowledge base
          </Button>
        </div>
      </>
    );
  }

  const crumbs = [
    { name: 'Home', path: '/' },
    { name: 'Knowledge', path: '/knowledge' },
    { name: article.title, path: `/knowledge/${article.slug}` },
  ];

  const linkedProducts = article.relatedProductSlugs
    .map((s) => products.find((p) => p.slug === s || p.slug === canonicalProductSlug(s)))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));
  const linkedCategories = article.relatedCategorySlugs
    .map((s) => categories.find((c) => c.slug === s))
    .filter((c): c is NonNullable<typeof c> => Boolean(c));
  const related = article.relatedSlugs
    .map((s) => knowledgeArticles.find((a) => a.slug === s))
    .filter((a): a is NonNullable<typeof a> => Boolean(a));

  return (
    <>
      <Seo
        title={article.title}
        description={article.summary}
        path={`/knowledge/${article.slug}`}
        type="article"
        keywords={article.keywords}
        updated={article.updated}
        jsonLd={[
          articleSchema({
            slug: article.slug,
            title: article.title,
            summary: article.summary,
            updated: article.updated,
            section: article.section,
          }),
          faqSchema(article.faq),
          breadcrumbSchema(crumbs),
        ]}
      />
      <PageHeader eyebrow={article.section} title={article.title} subtitle={article.summary} />

      <article className="container-x py-14">
        <div className="max-w-3xl">
          {/* The direct answer. First thing on the page, complete on its own. */}
          <p className="text-xl leading-relaxed text-ink">{article.answer}</p>

          {article.body.map((section) => (
            <section key={section.heading} className="mt-12">
              <h2 className="font-display text-h3 font-semibold text-ink">{section.heading}</h2>
              {section.paras?.map((p) => (
                <p key={p} className="mt-4 leading-relaxed text-ink-700">
                  {p}
                </p>
              ))}
              {section.terms?.length ? (
                <dl className="mt-5 divide-y divide-sand-200 border-y border-sand-200">
                  {section.terms.map((t) => (
                    <div key={t.term} className="py-4">
                      <dt className="font-semibold text-ink">{t.term}</dt>
                      <dd className="mt-1 leading-relaxed text-ink-600">{t.detail}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
              {section.bullets?.length ? (
                <ul className="mt-5 space-y-2.5">
                  {section.bullets.map((b) => (
                    <li key={b} className="flex gap-3 text-ink-700">
                      <Icon name="Check" className="mt-1 h-4 w-4 shrink-0 text-brand-500" />
                      <span className="leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}

          <FaqBlock items={article.faq} heading="Common questions" id="article-faq" />

          {article.sources.length > 0 && (
            <section className="mt-12" aria-labelledby="sources-heading">
              <h2 id="sources-heading" className="font-display text-h3 font-semibold text-ink">
                Where this comes from
              </h2>
              <p className="mt-2 text-sm text-ink-500">
                The explanations above are general agronomic science, drawn from the following bodies of
                work and instruments. Figures attributed to a GreenbayBio product come from that
                product’s own technical sheet and are marked as such.
              </p>
              <ul className="mt-4 space-y-2">
                {article.sources.map((s) => (
                  <li key={s} className="flex gap-3 text-sm text-ink-600">
                    <Icon name="FileText" className="mt-0.5 h-4 w-4 shrink-0 text-ink-300" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        {/* Products last, clearly separated, with no performance claim attached. */}
        {(article.rangeNote || linkedProducts.length > 0) && (
          <section className="mt-14 rounded-2xl border border-sand-200 bg-sand-100/50 p-8" aria-labelledby="range-heading">
            <h2 id="range-heading" className="font-display text-h3 font-semibold text-ink">
              Where the GreenbayBio range sits in this
            </h2>
            {article.rangeNote && <p className="mt-3 max-w-3xl leading-relaxed text-ink-600">{article.rangeNote}</p>}

            {linkedProducts.length > 0 && (
              <Reveal className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" stagger={0.06} y={16}>
                {linkedProducts.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/catalog/${p.slug}`}
                    className="group rounded-xl border border-sand-200 bg-white p-4 transition-colors hover:border-brand-400"
                  >
                    <span className="block font-semibold text-ink group-hover:text-brand-700">{p.name}</span>
                    {p.tagline && <span className="mt-0.5 block text-xs text-ink-400">{p.tagline}</span>}
                    <span className="mt-2 block text-sm leading-relaxed text-ink-500">{p.purpose}</span>
                  </Link>
                ))}
              </Reveal>
            )}

            {linkedCategories.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2.5">
                {linkedCategories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/product-categories/${c.slug}`}
                    className="inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 transition-colors hover:border-brand-400 hover:text-brand-700"
                  >
                    <span className="h-2 w-2 rounded-full" style={{ background: c.accentColor }} />
                    {c.name}
                  </Link>
                ))}
              </div>
            )}
          </section>
        )}

        {related.length > 0 && (
          <section className="mt-14" aria-labelledby="related-heading">
            <h2 id="related-heading" className="font-display text-h3 font-semibold text-ink">
              Related reading
            </h2>
            <ul className="mt-4 grid gap-3 md:grid-cols-3">
              {related.map((a) => (
                <li key={a.slug}>
                  <Link
                    to={`/knowledge/${a.slug}`}
                    className="block rounded-xl border border-sand-200 bg-white p-5 transition-colors hover:border-brand-400"
                  >
                    <span className="block font-semibold text-ink">{a.title}</span>
                    <span className="mt-1.5 block text-sm leading-relaxed text-ink-500">{a.summary}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </>
  );
}
